// Vite's `?worker` module has a generated default constructor; the import
// plugin does not understand that virtual-module contract.
// eslint-disable-next-line import-x/default
import SyntaxHighlightWorker from "./syntaxHighlight.worker?worker";
import type {
  HighlightRequest,
  HighlightResponse,
  SyntaxTokenLines,
} from "./syntaxHighlightProtocol";
import type { SyntaxLanguage } from "./syntaxLanguages";

export type HighlightedSources = {
  readonly newTokens: SyntaxTokenLines | null;
  readonly oldTokens: SyntaxTokenLines | null;
};

type Pending = {
  readonly reject: (reason: Error) => void;
  readonly resolve: (value: HighlightedSources) => void;
};

let nextRequestId = 1;
let worker: Worker | null = null;
const pending = new Map<number, Pending>();

const failPending = (message: string): void => {
  for (const request of pending.values()) {
    request.reject(new Error(message));
  }
  pending.clear();
  worker?.terminate();
  worker = null;
};

const getWorker = (): Worker => {
  if (worker) {
    return worker;
  }
  worker = new SyntaxHighlightWorker();
  worker.onmessage = (event: MessageEvent<HighlightResponse>) => {
    const response = event.data;
    const request = pending.get(response.id);
    if (!request) {
      return;
    }
    pending.delete(response.id);
    if ("error" in response) {
      request.reject(new Error(response.error));
    } else {
      request.resolve({
        newTokens: response.newTokens,
        oldTokens: response.oldTokens,
      });
    }
  };
  worker.onerror = () => failPending("Syntax highlighting worker failed.");
  return worker;
};

export const highlightSources = (
  language: SyntaxLanguage,
  oldSource: string | null,
  newSource: string | null,
): Promise<HighlightedSources> => {
  const id = nextRequestId++;
  return new Promise((resolve, reject) => {
    pending.set(id, { reject, resolve });
    const request: HighlightRequest = {
      id,
      language,
      newSource,
      oldSource,
    };
    getWorker().postMessage(request);
  });
};
